import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useLocalStorage from '../../../hooks/useLocalStorage'
import AuthContext from '../../../context/AuthContext'
import {
  ANALYTICS_EVENT,
  ANALYTICS_FEATURED_CARD_TRACKING_TYPE,
  CLICK_CARD_ONBOARDING_STEPS,
  GAME_CARD_OFFER_TYPE,
  LOCAL_STORAGE,
  USER_PLATFORM,
} from '../../../constants'
import { getUserPlatform, sendGA4Event } from '../../../utils/utils'

/**
 * The single way an offer is opened from /games.
 *
 * Card and CTA share it so a click is tracked once, advances onboarding once and navigates
 * once, whichever of the two the user pressed.
 *
 * @param {object} args.offer - the offer being opened
 * @param {number} args.index - its position in the row it is rendered in
 * @param {string} args.type - a GAME_CARD_OFFER_TYPE, which decides the featured tracking
 * @param {string} args.offerTileViewGUID - ties the click to the impression of the same tile
 * @param {object} [args.experiment] - { experimentId, variant } of the surface, when it is A/B
 */
const useOfferClick = ({ offer, index, type, offerTileViewGUID, experiment }) => {
  const { user, changeStep } = useContext(AuthContext)
  const [isDisabled, setDisabled] = useState(false)

  const navigate = useNavigate()
  const { setItem } = useLocalStorage()

  const offerCampaignId =
    getUserPlatform(user.platform) === USER_PLATFORM.ANDROID
      ? offer.campaignIdAndroid
      : offer.campaignIdIOS

  const handleClick = async () => {
    setDisabled(true)

    sendGA4Event(ANALYTICS_EVENT.SELECT_ITEM.OFFER, {
      item_id: offer.id,
      item_brand: user.platform === 'iOS' ? 'iTunes' : 'Google Play',
      index,
      is_activated: !!offer.activatedOffer,
      offer_campaign_id: offerCampaignId,
      ...experiment,
    })

    sendGA4Event(ANALYTICS_EVENT.OFFER_TILE_CLICK, {
      offerId: offer.id,
      is_ua_offer: offer.isUAOffer,
      offer_tile_view_guid: offerTileViewGUID,
      offer_campaign_id: offerCampaignId,
      ...experiment,
    })

    const openMMPLink = sessionStorage.getItem(LOCAL_STORAGE.OPEN_MMP_LINK)

    if (type === GAME_CARD_OFFER_TYPE.FEATURED) {
      sendGA4Event(ANALYTICS_EVENT.FEATURED_CARD_TRACKING, {
        event_type: openMMPLink ? 'tap' : 'view',
        featured_card_type: offer.activatedOffer
          ? ANALYTICS_FEATURED_CARD_TRACKING_TYPE.EXISTING_SPENDER_OFFER
          : ANALYTICS_FEATURED_CARD_TRACKING_TYPE.NEW_GAME_OFFER,
        offer_id: offer.id,
        unified_app_id: offer.unifiedAppId,
        placement_number: index + 1,
        offer_campaign_id: offerCampaignId,
        ...experiment,
      })
    }

    if (CLICK_CARD_ONBOARDING_STEPS.indexOf(user.onboardingStep) > -1) {
      setItem(LOCAL_STORAGE.OFFER_REWARDS_EXPLAINER_GAME, offer?.gameName)
      await changeStep(true, openMMPLink ? 2 : 1)
    }

    navigate(`/games/${offer.unifiedAppId}?offerId=${offer.id}`)

    setDisabled(false)
  }

  return { handleClick, isDisabled }
}

export default useOfferClick
